import { get, put, post, postJSON } from "api/base";
import Constants from "const/constants";

export const USER_ENDPOINT = `v1/users`;

const { USER_ROLES, USER_STATUS } = Constants;

export type CreateUserPayload = {
    name: string;
    email: string;
    role: ObjectValues<typeof USER_ROLES>;
    cognitoUserPassEnabled?: boolean;
    isMfaEnabled?: boolean;
    /** Array of shared integration ids */
    organizationCredentials: string[];
    /**
     * Comma separated string of assigned stream ids
     * (Format: "streamId,stream2Id")
     * Only required if non admin
     */
    streams?: string;
    /**
     * Comma separated string of assigned campaign ids
     * (Format: "contextType:contextId,contextType2:contextId2...")
     *  */
    campaigns?: string;
    /**
     * Filename of uploaded profile image
     *  */
    uploadedProfileImage?: string;
};

type MfaStatus = `inactive` | `active` | `pending`;

export type UserResponse = {
    /** From what I can tell this is always studio? */
    accountType: `studio`;
    cognitoUser?: boolean;
    cognitoUserPassEnabled?: boolean | null;
    creationDate: number;
    defaultCredential?: string | null;
    email: string;
    isMfaEnabled: boolean;
    lastAttemptedLogin?: number | null;
    lastLoggedIn?: number | null;
    loginAttempts?: number | null;
    mfaStatus: MfaStatus;
    name: string;
    organizationId: string;
    profileImageUrl?: string | null;
    roles: ObjectValues<typeof USER_ROLES>[];
    status: ObjectValues<typeof USER_STATUS>;
    udvrChunkMonitoringEnabled?: `true` | `false` | null;
    userId: string;
    /** @deprecated Mirrors name */
    username: string;
};

/**
 * Create, generate an invite email, and return the new user
 */
export const createUser = async (
    requestData: CreateUserPayload
): Promise<UserResponse> => {
    const payload = await postJSON(USER_ENDPOINT, requestData);

    // Currently casting this as our postJSON isn't typed
    return payload as unknown as UserResponse;
};

/**
 * @summary - Updates a user and returns the updated user object
 *
 * @param {String} id
 * @param {Object} requestData - The user properties/values to be updated
 * @async
 * @return {Promise<User>} An user object
 */
export const updateUser = async (id, requestData) => {
    const payload = await put(`${USER_ENDPOINT}/${id}`, {
        ...requestData
    });

    return payload;
};

/**
 * Returns a list of users
 */
export const getUsers = async (query?: string): Promise<UserResponse[]> => {
    const queryString = query ? `?${query}` : ``;
    const {
        data: { data }
    } = await get(`${USER_ENDPOINT}${queryString}`);
    return data;
};

/**
 * Returns a list of users with access to a specific stream
 */
export const getUsersForStream = async (
    streamId: string
): Promise<UserResponse[]> => {
    const data = await getUsers(`streamId=${streamId}`);
    return data;
};

/**
 * Returns a list of users with access to a specific campaign
 */
export const getUsersForCampaign = async (
    campaignId: string
): Promise<UserResponse[]> => {
    const data = await getUsers(`contextId=gallery:${campaignId}`);
    return data;
};

/**
 * Sets the target users mfaStatus to `pending` and also
 * sends an email instructing the user to reset/setup their MFA device
 */
export const resetMfa = async (id: string) =>
    post(`ws/studiousers/${id}/resetmfa`);

/**
 * @summary - Re sends an activation invite to a pending user
 *
 * @param {String} id
 * @async
 */
export const resendInvite = async (id) => {
    await post(`${USER_ENDPOINT}/${id}/invite`);
};

export const validateEmail = async (email) => {
    const {
        data: { userUpdate }
    } = await post(`ws/tools/validator`, {
        resource: `userUpdate`,
        inputs: JSON.stringify([
            {
                name: `email`,
                value: email
            }
        ])
    });

    if (userUpdate[0].isValid) {
        return userUpdate[0];
    }
    return Promise.reject(userUpdate[0]);
};
